const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;
const CHANNEL_ID = "UCQyqh-TuRIgqGM6-6pG9pag";
const BASE_URL = "https://www.googleapis.com/youtube/v3";

// ======================
// 🔹 Cache (Memory + localStorage)
// ======================
const cache = {};
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

const getFromCache = (key) => {
  const entry = cache[key];
  if (entry && Date.now() - entry.timestamp < CACHE_TTL) return entry.data;

  const stored = localStorage.getItem(`yt_cache_${key}`);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      if (Date.now() - parsed.timestamp < CACHE_TTL) {
        cache[key] = parsed;
        return parsed.data;
      } else {
        localStorage.removeItem(`yt_cache_${key}`);
      }
    } catch {
      localStorage.removeItem(`yt_cache_${key}`);
    }
  }
  return null;
};

const setCache = (key, data) => {
  const entry = { data, timestamp: Date.now() };
  cache[key] = entry;
  localStorage.setItem(`yt_cache_${key}`, JSON.stringify(entry));
};

// ======================
// 🔹 Helpers
// ======================

const fetchWithCache = async (cacheKey, url) => {
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const res = await fetch(url);
  const data = await res.json();
  const items = data.items || [];

  setCache(cacheKey, items);
  return items;
};

// ✅ FIX: Always extract real videoId correctly
const formatVideo = (item) => {
  const snippet = item.snippet;
  if (!snippet?.title) return null;

  const title = snippet.title.trim().toLowerCase();
  if (title === "private video" || title === "deleted video") return null;

  const thumbnails = snippet.thumbnails || {};

  // 🔑 Correct videoId extraction
  let videoId =
    snippet.resourceId?.videoId || // playlistItems API
    item.contentDetails?.videoId || // some APIs
    item.id?.videoId || // search API
    (typeof item.id === "string" ? item.id : null); // fallback only if plain videoId string

  if (!videoId) return null;

  return {
    title: snippet.title,
    videoId,
    thumbnail:
      thumbnails.maxres?.url ||
      thumbnails.standard?.url ||
      thumbnails.high?.url ||
      thumbnails.medium?.url ||
      thumbnails.default?.url ||
      "",
    channelTitle: snippet.channelTitle || "",
  };
};

// Filter valid videos only
const filterValidVideos = (items) =>
  items
    .map((v) => {
      const snippet = v.snippet;
      const status = v.status;

      if (!snippet?.title) return null;

      const title = snippet.title.trim().toLowerCase();
      if (title === "private video" || title === "deleted video") return null;

      // ✅ Always get correct videoId from snippet.resourceId.videoId
      const videoId =
        snippet.resourceId?.videoId ||
        v.contentDetails?.videoId ||
        v.id?.videoId ||
        (typeof v.id === "string" ? v.id : null);

      if (!videoId) return null;
      if (status?.privacyStatus && status.privacyStatus !== "public") return null;

      const thumbnails = snippet.thumbnails || {};

      return {
        title: snippet.title,
        videoId,
        thumbnail:
          thumbnails.maxres?.url ||
          thumbnails.standard?.url ||
          thumbnails.high?.url ||
          thumbnails.medium?.url ||
          thumbnails.default?.url ||
          "",
        channelTitle: snippet.channelTitle || "",
      };
    })
    .filter((v) => v && v.videoId && v.title);



// ======================
// 🔹 URL Helpers
// ======================

/**
 * Extracts Video ID and returns a clean Embed URL
 * Supports: watch?v=..., youtu.be/..., embed/..., or plain ID
 */
export const getEmbedUrl = (input) => {
  if (!input) return "";
  let videoId = "";

  try {
    if (input.includes("watch?v=")) {
      videoId = new URL(input).searchParams.get("v");
    } else if (input.includes("youtu.be/")) {
      videoId = input.split("youtu.be/")[1]?.split(/[?#]/)[0];
    } else if (input.includes("embed/")) {
      videoId = input.split("embed/")[1]?.split(/[?#]/)[0];
    } else {
      videoId = input.trim();
    }
  } catch (e) {
    videoId = input.trim();
  }

  return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
};

// ======================
// 🔹 API Methods
// ======================

export const getChannelStatistics = async () => {
  const cacheKey = "channel_stats";
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  try {
    const res = await fetch(
      `${BASE_URL}/channels?part=statistics&id=${CHANNEL_ID}&key=${API_KEY}`
    );
    const data = await res.json();

    if (!data.items || data.items.length === 0) return null;

    const stats = data.items[0].statistics;
    const result = {
      subscriberCount: stats.subscriberCount,
      videoCount: stats.videoCount,
      viewCount: stats.viewCount,
    };

    setCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error("Error fetching channel stats:", error);
    return null;
  }
};

export const getPlaylists = () =>
  fetchWithCache(
    "playlists",
    `${BASE_URL}/playlists?part=snippet&channelId=${CHANNEL_ID}&maxResults=25&key=${API_KEY}`
  );

export const getVideosInPlaylist = async (playlistId) => {
  const items = await fetchWithCache(
    `playlist_${playlistId}`,
    `${BASE_URL}/playlistItems?part=snippet,status&playlistId=${playlistId}&maxResults=50&key=${API_KEY}`
  );
  return filterValidVideos(items);
};

// Helper: ISO 8601 duration (PT1M12S) → seconds
const parseDurationToSeconds = (duration) => {
  if (!duration) return 0;

  const match = duration.match(/PT(?:(\d+)M)?(?:(\d+)S)?/);
  const minutes = match?.[1] ? parseInt(match[1], 10) : 0;
  const seconds = match?.[2] ? parseInt(match[2], 10) : 0;

  return minutes * 60 + seconds;
};

export const getShorts = async () => {
  try {
    let allItems = [];
    let nextPageToken = "";

    // 1. Fetch all videoIds from channel
    do {
      const res = await fetch(
        `${BASE_URL}/search?key=${API_KEY}&channelId=${CHANNEL_ID}&part=snippet,id&order=date&maxResults=50&type=video&pageToken=${nextPageToken}`
      );
      const data = await res.json();

      const items = data.items || [];
      allItems = [...allItems, ...items];

      nextPageToken = data.nextPageToken || "";
    } while (nextPageToken);

    // 2. Extract videoIds
    const videoIds = allItems
      .map((item) => item.id?.videoId || item.id)
      .filter(Boolean);

    if (videoIds.length === 0) return [];

    // 3. Fetch video details in batches of 50
    const detailedItems = [];
    for (let i = 0; i < videoIds.length; i += 50) {
      const batch = videoIds.slice(i, i + 50);
      const detailsRes = await fetch(
        `${BASE_URL}/videos?part=snippet,contentDetails&id=${batch.join(",")}&key=${API_KEY}`
      );
      const detailsData = await detailsRes.json();
      detailedItems.push(...(detailsData.items || []));
    }

    // 4. Filter only real Shorts (≤ 60s)
    const shortsOnly = detailedItems.filter((video) => {
      const totalSeconds = parseDurationToSeconds(video.contentDetails?.duration);
      return totalSeconds > 0 && totalSeconds <= 60;
    });

    // 5. Return formatted result
    return filterValidVideos(shortsOnly);
  } catch (err) {
    console.error("Error fetching shorts:", err);
    return [];
  }
};

export const getPlaylistVideosByTitle = async (targetTitle) => {
  const cacheKey = `playlistByTitle_${targetTitle}`;
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  let nextPageToken = "";
  let playlistId = null;

  // 1️⃣ Find playlist by title
  do {
    const res = await fetch(
      `${BASE_URL}/playlists?part=snippet&channelId=${CHANNEL_ID}&maxResults=50&key=${API_KEY}&pageToken=${nextPageToken}`
    );
    const data = await res.json();
    if (!data.items) break;

    const found = data.items.find(
      (p) => p.snippet?.title?.trim() === targetTitle
    );
    if (found) {
      playlistId = found.id;
      break;
    }

    nextPageToken = data.nextPageToken || "";
  } while (nextPageToken);

  if (!playlistId) return [];

  // 2️⃣ Fetch all videos in that playlist
  let videos = [];
  nextPageToken = "";
  do {
    const res = await fetch(
      `${BASE_URL}/playlistItems?part=snippet,status&playlistId=${playlistId}&maxResults=50&key=${API_KEY}&pageToken=${nextPageToken}`
    );
    const data = await res.json();
    if (!data.items) break;

    videos = videos.concat(filterValidVideos(data.items));
    nextPageToken = data.nextPageToken || "";
  } while (nextPageToken);

  setCache(cacheKey, videos);
  return videos;
};

const getPlaylistIdFromUrl = (url) => {
  const urlObj = new URL(url);
  return urlObj.searchParams.get("list");
};
export const getLatestLiveVideo = async () => {
  const cacheKey = "latest_live_video";
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  try {
    // 1️⃣ First, check for active live streams
    const liveRes = await fetch(
      `${BASE_URL}/search?part=snippet&channelId=${CHANNEL_ID}&type=video&eventType=live&key=${API_KEY}`
    );
    const liveData = await liveRes.json();

    if (liveData.items && liveData.items.length > 0) {
      const videoId = liveData.items[0].id.videoId;
      const url = `https://www.youtube.com/embed/${videoId}`;
      setCache(cacheKey, url);
      return url;
    }

    // 2️⃣ If no live stream, get the most recent video (including completed live streams)
    const recentRes = await fetch(
      `${BASE_URL}/search?part=snippet&channelId=${CHANNEL_ID}&type=video&order=date&maxResults=1&key=${API_KEY}`
    );
    const recentData = await recentRes.json();

    if (recentData.items && recentData.items.length > 0) {
      const videoId = recentData.items[0].id.videoId;
      const url = `https://www.youtube.com/embed/${videoId}`;
      setCache(cacheKey, url);
      return url;
    }

    return null;
  } catch (error) {
    console.error("Error fetching latest live video:", error);
    return null;
  }
};

export const getFirst7PlaylistVideosByTitle = async (playlistUrl) => {
  try {
    const playlistId = getPlaylistIdFromUrl(playlistUrl);
    if (!playlistId) throw new Error("Invalid playlist URL");

    const response = await fetch(
      `${BASE_URL}/playlistItems?part=snippet&maxResults=7&playlistId=${playlistId}&key=${API_KEY}`
    );

    const data = await response.json();

    if (!data.items) return [];

    return data.items.map((item) => ({
      title: item.snippet.title,
      videoLink: `https://www.youtube.com/watch?v=${item.snippet.resourceId.videoId}`,
      thumbnail: item.snippet.thumbnails.high.url
    }));
  } catch (error) {
    console.error("Error fetching playlist videos:", error);
    return [];
  }
};
